// src/context/MenuContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import {
  collectionGroup,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { groupByCategory } from "../utils/groupByCategory";
import { storage } from "../utils/storage";

const MenuContext = createContext();
const MENU_STORAGE_KEY = 'menu_data';

export const useMenu = () => useContext(MenuContext);

export const MenuProvider = ({ children }) => {
  const [groupedMenu, setGroupedMenu] = useState({});
  const [loading, setLoading] = useState(true);
  const isFetching = useRef(false);

  const fetchMenu = async (forceRefresh = false) => {
    if (isFetching.current) return;
    isFetching.current = true;

    try {
      // Try to load from storage first if not forcing refresh
      if (!forceRefresh) {
        const cachedData = storage.get(MENU_STORAGE_KEY);
        if (cachedData) {
          setGroupedMenu(cachedData);
          setLoading(false);
          isFetching.current = false;
          return;
        }
      }

      setLoading(true);
      const querySnapshot = await getDocs(collectionGroup(db, "menuItems"));
      const menuItemsWithVendor = [];

      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        const pathSegments = docSnap.ref.path.split("/");
        const vendorId = pathSegments[1];

        let vendorName = "Unknown Vendor";
        try {
          const vendorRef = doc(db, "vendors", vendorId);
          const vendorSnap = await getDoc(vendorRef);
          if (vendorSnap.exists()) {
            vendorName = vendorSnap.data().shopName;
          }
        } catch {}

        menuItemsWithVendor.push({
          id: docSnap.id,
          vendorId,
          vendorName,
          ...data,
        });
      }

      const groupedData = groupByCategory(menuItemsWithVendor);
      setGroupedMenu(groupedData);
      storage.set(MENU_STORAGE_KEY, groupedData);
    } catch (err) {
      console.error("Error fetching menu:", err);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  };

  useEffect(() => {
    fetchMenu();
  }, []);

  const refreshMenu = () => {
    fetchMenu(true);
  };

  return (
    <MenuContext.Provider value={{ groupedMenu, loading, refreshMenu }}>
      {children}
    </MenuContext.Provider>
  );
};
